/**
 * services/NoteDraftService.ts
 * Service for managing note drafts during live trading sessions
 *
 * Extends BaseService to provide CRUD operations for NoteDraft entities.
 * Note drafts are transient notes attached to owning entities (sessions, trades,
 * economic events, or objectives) that get converted to permanent Notes when
 * the session is finalized.
 */

import { BaseService } from './BaseService';
import { isTombstoned } from '@/utils/tombstone';
import type { NoteDraft, NoteDraftType } from '@/types/transient/NoteDraft';

/**
 * Service for managing NoteDraft entities
 *
 * Provides CRUD operations plus owner-based lookup for cascade delete support.
 * Inherits add(), get(), getAll(), update(), softDelete(), hardDelete() from BaseService.
 */
export class NoteDraftService extends BaseService<NoteDraft> {
  constructor() {
    super('note_drafts');
  }

  /**
   * Get all note drafts for a specific owner
   *
   * Uses the compound index [noteType+ownerId] for efficient lookup.
   * Filters out tombstoned (soft-deleted) records.
   *
   * @param noteType - The type of note (determines owner table)
   * @param ownerId - The ID of the owning entity
   * @returns Promise resolving to array of note drafts for the owner
   */
  async getByOwner(noteType: NoteDraftType, ownerId: string): Promise<NoteDraft[]> {
    try {
      const records = await this.table
        .where('[noteType+ownerId]')
        .equals([noteType, ownerId])
        .toArray();

      return records.filter(record => !isTombstoned(record));
    } catch (error) {
      console.error(
        `Failed to get note_drafts for ${noteType}/${ownerId}:`,
        error
      );
      throw new Error('Failed to retrieve note drafts');
    }
  }

  /**
   * Soft-delete all non-tombstoned note drafts for a specific owner.
   *
   * Used to clean up orphaned note drafts when a trade drawer is cancelled
   * before the trade is committed to the session.
   *
   * @param noteType - The type of note (determines owner table)
   * @param ownerId - The ID of the owning entity
   */
  async softDeleteByOwner(noteType: NoteDraftType, ownerId: string): Promise<void> {
    const notes = await this.getByOwner(noteType, ownerId);
    await Promise.all(notes.map((note) => this.softDelete(note.id)));
  }
}

// Export singleton instance
export const noteDraftService = new NoteDraftService();
